/**
 * The diner-journey flow machine (tasks/diner-booking-ui.md tech decision: hand-rolled
 * discriminated-union reducer, no router — the widget runs as an island/iframe and must not fight the
 * host page's URL/history). Steps: SEARCH → SLOTS → DETAILS → CONFIRMED | PENDING | BLOCKED |
 * CONTACT_FALLBACK. The reducer is PURE and exhaustive (idempotency keys are minted by the caller and
 * passed IN, so transitions are deterministic and table-testable), and every rejection re-enters SLOTS
 * with fresh alternatives — never a dead end (R7/R8).
 */

export type FlowStep =
  | "SEARCH"
  | "SLOTS"
  | "DETAILS"
  | "GUARANTEE"
  | "CONFIRMED"
  | "PENDING"
  | "BLOCKED"
  | "CONTACT_FALLBACK";

export interface SearchParams {
  /** The venue the journey currently targets — changes ONLY on an explicit overflow tap (R6). */
  venueId: string;
  serviceDay: string | null;
  partySize: number;
}

export interface SelectedSlot {
  serviceId: string;
  startSec: number;
  durationSec: number;
}

export interface HoldInfo {
  holdId: string;
  /** Server truth for the countdown (§D.4): remaining time is always derived from this, never a timer. */
  expiresAtSec: number;
}

export interface Confirmation {
  status: "CONFIRMED" | "PENDING";
  /** The created booking's id — the guarantee card step (card-fingerprint-no-show.md) keys on it. */
  bookingId: string;
  /** The venue the booking was made at — the journey's, which an overflow tap may have switched (R6). */
  venueId: string;
  manageToken: string;
  email?: string;
  serviceId: string;
  startSec: number;
  durationSec: number;
  partySize: number;
}

/**
 * The no-show guarantee a created-but-not-yet-confirmed booking carries (card-fingerprint-no-show.md).
 * The requirement + snapshot fee + free-cancellation cutoff are evaluated on the booking write path from
 * `Service.requireCreditCard` and returned by the create response (SC-1 — snapshotted, never recomputed).
 * `paymentStatus` NOT_PROVIDED → PROVIDED once the card step succeeds; only then may the booking confirm.
 */
export interface GuaranteeInfo {
  venueId: string;
  bookingId: string;
  /** No-show fee in integer minor units (ADR-0024 — never a float). */
  feeMinor: number;
  currency: string;
  /** The instant free cancellation closes (epoch sec), or null when no cutoff is configured. */
  cutoffAtSec: number | null;
  /** The versioned guarantee-terms policy the consent leg rides with (R7). */
  policyVersion: string;
}

export type FlowState =
  | { step: "SEARCH"; search: SearchParams }
  | { step: "SLOTS"; search: SearchParams; releasedNotice: boolean; alternativesOnly: boolean }
  | {
      step: "DETAILS";
      search: SearchParams;
      slot: SelectedSlot | null;
      hold: HoldInfo | null;
      /** Over-max party (SC-5): the request variant of DETAILS — no slot query was ever issued. */
      requestVariant: boolean;
      /** The ONE idempotency key for this submission (BK-2); reminted on edit / new slot. */
      idempotencyKey: string;
    }
  | {
      /**
       * Card-guarantee gate (card-fingerprint-no-show.md): a guarantee-required booking is CREATED but
       * cannot reach CONFIRMED/PENDING until the SetupIntent card step succeeds. Carries the pending
       * confirmation to promote on success.
       */
      step: "GUARANTEE";
      guarantee: GuaranteeInfo;
      confirmation: Confirmation;
    }
  | { step: "CONFIRMED"; confirmation: Confirmation }
  | { step: "PENDING"; confirmation: Confirmation }
  | { step: "BLOCKED" }
  | { step: "CONTACT_FALLBACK"; rejectionCode: string };

export type FlowAction =
  | { type: "SET_DATE"; serviceDay: string | null }
  | { type: "SET_PARTY"; partySize: number }
  | { type: "SEARCH_SUBMIT" }
  | { type: "START_REQUEST"; idempotencyKey: string }
  | { type: "SELECT_SLOT"; slot: SelectedSlot; hold: HoldInfo; idempotencyKey: string }
  | { type: "HOLD_EXPIRED" }
  | { type: "SLOT_TAKEN" }
  | { type: "REJECT_TO_SLOTS" }
  | { type: "REMINT"; idempotencyKey: string }
  | { type: "SUBMIT_CONFIRMED"; confirmation: Confirmation }
  | { type: "SUBMIT_PENDING"; confirmation: Confirmation }
  | { type: "REQUIRE_GUARANTEE"; guarantee: GuaranteeInfo; confirmation: Confirmation }
  | { type: "GUARANTEE_SAVED" }
  | { type: "BANNED" }
  | { type: "CONTACT_FALLBACK"; rejectionCode: string }
  | { type: "SWITCH_VENUE"; venueId: string }
  | { type: "BACK" };

export function initialFlowState(
  venueId: string,
  partySize = 2,
  serviceDay: string | null = null,
): FlowState {
  return { step: "SEARCH", search: { venueId, serviceDay, partySize } };
}

/** Read the current search params regardless of step (SEARCH/SLOTS/DETAILS carry them). */
export function searchOf(state: FlowState): SearchParams | null {
  switch (state.step) {
    case "SEARCH":
    case "SLOTS":
    case "DETAILS":
      return state.search;
    default:
      return null;
  }
}

function slotsState(
  search: SearchParams,
  opts: { releasedNotice?: boolean; alternativesOnly?: boolean } = {},
): FlowState {
  return {
    step: "SLOTS",
    search,
    releasedNotice: opts.releasedNotice ?? false,
    alternativesOnly: opts.alternativesOnly ?? false,
  };
}

/**
 * The one transition function. Actions that don't apply to the current step are no-ops (return the
 * same reference) — defensive against races (a late HOLD_EXPIRED after the diner already left DETAILS).
 */
export function flowReducer(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case "SET_DATE":
      return state.step === "SEARCH"
        ? { ...state, search: { ...state.search, serviceDay: action.serviceDay } }
        : state;

    case "SET_PARTY":
      return state.step === "SEARCH"
        ? { ...state, search: { ...state.search, partySize: action.partySize } }
        : state;

    case "SEARCH_SUBMIT":
      return state.step === "SEARCH" && state.search.serviceDay !== null
        ? slotsState(state.search)
        : state;

    case "START_REQUEST":
      return state.step === "SEARCH"
        ? {
            step: "DETAILS",
            search: state.search,
            slot: null,
            hold: null,
            requestVariant: true,
            idempotencyKey: action.idempotencyKey,
          }
        : state;

    case "SELECT_SLOT":
      return state.step === "SLOTS"
        ? {
            step: "DETAILS",
            search: state.search,
            slot: action.slot,
            hold: action.hold,
            requestVariant: false,
            idempotencyKey: action.idempotencyKey,
          }
        : state;

    case "HOLD_EXPIRED":
      // Mid-form expiry (R7): fall back to SLOTS with the released notice + alternatives-first view.
      return state.step === "DETAILS" && !state.requestVariant
        ? slotsState(state.search, { releasedNotice: true, alternativesOnly: true })
        : state;

    case "SLOT_TAKEN":
      // The slot was claimed during hold placement (still on SLOTS): swap to fresh alternatives.
      return state.step === "SLOTS"
        ? slotsState(state.search, { releasedNotice: true, alternativesOnly: true })
        : state;

    case "REJECT_TO_SLOTS":
      // Concurrent loss / covers-cap at submit (R7/R8): back to SLOTS showing fresh alternatives.
      return state.step === "DETAILS"
        ? slotsState(state.search, { releasedNotice: true, alternativesOnly: true })
        : state;

    case "REMINT":
      return state.step === "DETAILS" ? { ...state, idempotencyKey: action.idempotencyKey } : state;

    case "SUBMIT_CONFIRMED":
      return state.step === "DETAILS"
        ? { step: "CONFIRMED", confirmation: action.confirmation }
        : state;

    case "SUBMIT_PENDING":
      return state.step === "DETAILS"
        ? { step: "PENDING", confirmation: action.confirmation }
        : state;

    case "REQUIRE_GUARANTEE":
      // A guarantee-required booking was created; gate it behind the card step before confirming (R7).
      return state.step === "DETAILS"
        ? { step: "GUARANTEE", guarantee: action.guarantee, confirmation: action.confirmation }
        : state;

    case "GUARANTEE_SAVED":
      // Card saved (paymentStatus PROVIDED) — promote to the end screen the pending confirmation names.
      return state.step === "GUARANTEE"
        ? {
            step: state.confirmation.status === "CONFIRMED" ? "CONFIRMED" : "PENDING",
            confirmation: state.confirmation,
          }
        : state;

    case "BANNED":
      // Neutral blocked screen (R5, GU-4) — reachable only from a submit attempt.
      return state.step === "DETAILS" ? { step: "BLOCKED" } : state;

    case "CONTACT_FALLBACK":
      // OnlineBookingDisabled / VenueNotLive at any read/write point → venue contact fallback.
      return { step: "CONTACT_FALLBACK", rejectionCode: action.rejectionCode };

    case "SWITCH_VENUE": {
      // Overflow tap (R6): the ONLY place venueId changes. Re-enter SLOTS on the sister venue.
      const search = searchOf(state);
      return search ? slotsState({ ...search, venueId: action.venueId }) : state;
    }

    case "BACK":
      if (state.step === "SLOTS") return { step: "SEARCH", search: state.search };
      if (state.step === "DETAILS") {
        return state.requestVariant
          ? { step: "SEARCH", search: state.search }
          : slotsState(state.search);
      }
      return state;

    default:
      return assertNever(action);
  }
}

function assertNever(action: never): never {
  throw new Error(`unhandled flow action: ${JSON.stringify(action)}`);
}
